import { CACHE_SHARE_BASE_URL } from "../../../../constants";
import axios from "axios";

export const fetchAthletes = async ({
  team,
  selectedSport,
  revenueAmount,
  name = undefined,
}) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(`${CACHE_SHARE_BASE_URL}/athletes`, {
      params: {
        sport: selectedSport?.value || undefined,
        team: team?.value || undefined,
        gender: selectedSport?.gender || undefined,
        budget: revenueAmount ? revenueAmount.replace(/,/g, "") : undefined,
        name: name,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`
      );
    }
    throw error;
  }
};

export const fetchCacheShareRanges = async ({
  rosterTeamId,
  athleteEmails,
  revshareBudget,
  selectedOnly = false,
}) => {
  const token = localStorage.getItem("token");
  try {
    const payload = {
      rosterTeamId,
      athleteEmails,
      revshareBudget: revshareBudget
        ? revshareBudget.replace(/,/g, "")
        : undefined,
    };

    if (selectedOnly) {
      payload.selectedOnly = true;
    }

    const response = await axios.post(
      `${CACHE_SHARE_BASE_URL}/calculator/cacheshare`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`
      );
    }
    throw error;
  }
};

// The allocation board's revenue share locks ({rowId: amount}, or
// [{id, amount}]) and hidden players for a calculator payload, only when
// there are some. The calculators answer each row's payoutAllocated and
// isLocked, and the allocation totals, from them. The Add player roster
// preview sends neither.
const hasEntries = (value) => (Array.isArray(value) ? value.length > 0 : !!value && typeof value === "object" && Object.keys(value).length > 0);
const lockFields = ({ lockedAllocations, hiddenIds }) => ({
  ...(hasEntries(lockedAllocations) ? { lockedAllocations } : {}),
  ...(hasEntries(hiddenIds) ? { hiddenIds } : {}),
});

export const fetchCacheShareRangesSportsradar = async ({
  rosterTeamId,
  revshareBudget,
  athleteIds,
  selectedOnly = false,
  importedAthleteIds,
  importedAthletes, // new shape: [{id, source}] supports cross-level imports
  sport,
  lockedAllocations,
  hiddenIds,
}) => {
  const token = localStorage.getItem("token");
  try {
    const payload = {
      rosterTeamId,
      revshareBudget,
      athleteIds,
      selectedOnly,
      importedAthleteIds,
      importedAthletes,
      sport,
      ...lockFields({ lockedAllocations, hiddenIds }),
    };

    const response = await axios.post(
      `${CACHE_SHARE_BASE_URL}/calculator/cacheshare/sportsradar`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`
      );
    }
    throw error;
  }
};

// Live SR CacheShare — every request hits the SR API directly (no cache_sr_
// reads). Used for NCAA D-I men's / women's basketball so coaches always see
// current-season rosters + stats. Falls back to the prior season per player
// when current-season games_played is 0, and the response flags that with
// `isPreviousSeason` + `seasonYear` so the grid can render the PS chip.
//
// Cross-source imports: `importedAthletes: [{id, source}]` lets a coach
// import HS / EYBL / JUCO / pro players into an NCAA D-I CacheShare grid.
// SR imports get folded into the live SR profile pool; Cerebro imports get
// pulled from cache_cb_* by the backend.
export const fetchCacheShareRangesSportsradarLive = async ({
  rosterTeamId,
  revshareBudget,
  athleteIds,
  selectedOnly = false,
  importedAthleteIds,
  importedAthletes,
  sport,
  league,
  lockedAllocations,
  hiddenIds,
}) => {
  const token = localStorage.getItem("token");
  try {
    const payload = {
      rosterTeamId,
      revshareBudget,
      athleteIds,
      selectedOnly,
      importedAthleteIds,
      importedAthletes,
      sport,
      league,
      ...lockFields({ lockedAllocations, hiddenIds }),
    };

    const response = await axios.post(
      `${CACHE_SHARE_BASE_URL}/calculator/cacheshare/sportsradar/live/`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`
      );
    }
    throw error;
  }
};

export const searchAthletesSportsradar = async ({
  firstName,
  lastName,
  teamName,
  teamAlias,
  teamMarket,
  position,
  jersey,
  status,
  sport,
  page,
}) => {
  const token = localStorage.getItem("token");

  try {
    const params = {
      first_name: firstName ? firstName?.trim() : undefined,
      last_name: lastName ? lastName?.trim() : undefined,
      team_name: teamName ? teamName?.trim() : undefined,
      team_alias: teamAlias ? teamAlias?.trim() : undefined,
      team_market: teamMarket ? teamMarket?.trim() : undefined,
      position: position ? position?.trim() : undefined,
      jersey: jersey ? jersey?.trim() : undefined,
      status: status ? status?.trim() : undefined,
      sport: sport ?? undefined,
      page: page ?? undefined,
    };

    const response = await axios.get(
      `${CACHE_SHARE_BASE_URL}/athletes/sportsradar`,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`
      );
    }
    throw error;
  }
};

// Tag every team object with its data source so CacheShare can route the
// calculator + search calls to the right backend (SportRadar vs Cerebro).
const tagSource = (teams, source) =>
  (Array.isArray(teams) ? teams : []).map((t) => ({ ...t, source }));

export const fetchTeamOptions = async ({
  sortBy = "market",
  order = "asc",
  sport,
}) => {
  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  try {
    const response = await axios.get(
      `${CACHE_SHARE_BASE_URL}/sportsradar/teams?sport=${sport}&sortBy=${sortBy}&order=${order}`,
      { headers }
    );
    return tagSource(response.data, "sportsradar");
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`
      );
    }
    throw error;
  }
};

export const fetchTeamById = async ({
  sport,
  teamId
}) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(
      `${CACHE_SHARE_BASE_URL}/sportsradar/teams/${teamId}?sport=${sport}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const team = response.data;
    return team ? { ...team, source: "sportsradar" } : team;
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`
      );
    }
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Cerebro (High School / EYBL grassroots) endpoints
// ---------------------------------------------------------------------------

// Cerebro returns { id, name, city } but the existing team dropdown expects
// { id, market, name, alias }. Pass through what we have — no synthetic
// "EYBL"/"17U" fallbacks, since they leaked into UI labels (e.g. the
// CacheShare ranges header rendered "EYBL Basketball (M)" for an NBA team).
// Cerebro teams that lack a city just render with a blank market; the team
// name itself carries enough identity (e.g. "Golden State Warriors").
//
// The name fields the server adds to a picker row (10/19 notes, D15): the
// name as people read it (a squad's age group, no season), the age group,
// and the season of its latest event, which pickers show as a subline. Only
// the fields the row has are kept.
const nameFieldsOf = (team) => Object.fromEntries(
  [["displayName", team.displayName], ["ageGroup", team.ageGroup], ["season", team.season]].filter(([, v]) => v),
);

// A Cerebro program merged with a CACHE Partner team comes back tagged with
// the partner team (cacheTeamId): route it as that partner team so the
// roster is the coach's list with live Cerebro stats, and it appears once.
const normalizeCerebroTeam = (team) =>
  team.cacheTeamId
    ? {
        id: team.cacheTeamId,
        team_id: team.cacheTeamId,
        name: team.name || "",
        market: team.city || "",
        alias: "",
        source: "cache_teams",
        cacheLeague: team.cacheLeague || null,
        league_key: team.cacheLeague || null,
        partner_program: team.partnerProgram || "",
        mergedCerebroProgramId: team.id,
        ...nameFieldsOf(team),
      }
    : {
        id: team.id,
        name: team.name || "",
        market: team.city || "",
        alias: "",
        source: "cerebro",
        // Exact squad scope ("competition|division"): the calculators
        // roster and score this squad, not the whole program.
        leagueKey: team.leagueKey || null,
        // The squad's own catalogue key: what a team binding and its
        // roster read are filed under (the list's cerebroLeague tag is the
        // scope's first key, which names another squad of a multi-squad
        // scope).
        ...(team.catalogueKey ? { catalogueKey: team.catalogueKey } : {}),
        // A program with no competition on file (true): bound through a
        // provisional CACHE team (D7). False on a row with no league says
        // its competitions could not be checked (the team feed down), so
        // it is not bound as a program (pickedTeamBinding).
        ...(typeof team.programOnly === "boolean" ? { programOnly: team.programOnly } : {}),
        ...nameFieldsOf(team),
      };

// Lists each Cerebro sport-key whose teams cache has at least one doc, so the
// CacheShare chip row only ever renders levels we can actually show data for.
export const fetchCerebroLevels = async () => {
  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  try {
    const response = await axios.get(`${CACHE_SHARE_BASE_URL}/cerebro/levels`, {
      headers,
    });
    return (response.data && response.data.levels) || [];
  } catch (error) {
    console.warn("Cerebro levels unavailable:", error?.message || error);
    return [];
  }
};

// CACHE Partners team query — admin-managed teams. Hits
// /api/users/cache-teams/teams, optionally filtered by gender, partner
// program, or league_key. A failure reads as no teams unless the caller
// opts into `throwOnError`.
export const fetchCacheTeams = async ({ gender, league, partner } = {}, { throwOnError = false } = {}) => {
  const token = localStorage.getItem("token");
  try {
    const params = {};
    if (gender) params.gender = gender;
    if (league) params.league = league;
    if (partner) params.partner = partner;
    const response = await axios.get(
      `${CACHE_SHARE_BASE_URL}/cache-teams/teams`,
      { params, headers: { Authorization: `Bearer ${token}` } },
    );
    return (response.data && response.data.teams) || [];
  } catch (error) {
    if (throwOnError) throw error;
    console.warn("Cache Partners team fetch failed:", error?.message || error);
    return [];
  }
};

// CACHE Partner program list — distinct partner_program values across
// cache_teams. CacheShareFilters merges these into the Step 4 body list so
// each program (e.g. "Under Armour Next") gets its own option with the
// CACHE logo badge.
export const fetchCachePartners = async ({ gender } = {}) => {
  const token = localStorage.getItem("token");
  try {
    const params = {};
    if (gender) params.gender = gender;
    const response = await axios.get(
      `${CACHE_SHARE_BASE_URL}/cache-teams/partners`,
      { params, headers: { Authorization: `Bearer ${token}` } },
    );
    return (response.data && response.data.partners) || [];
  } catch (error) {
    console.warn("Cache Partners list fetch failed:", error?.message || error);
    return [];
  }
};

// A team list's dataStatus when the team feed did not fully answer it:
// {state 'stale' | 'unavailable', message, asOf?} (users/cerebro_views.py
// _search_status: a saved list, the teams already on CACHE, or a scope that
// answered in part). null for a live list.
const limitedListStatus = (status) =>
  status && typeof status === "object" && status.state && status.state !== "live" ? status : null;

// The dataStatus a team list carries: its own (withListStatus), else its
// first tagged row's (allocationModel.teamsDataStatus reads it the same way).
const listStatusOf = (list) => list?.dataStatus || (list || []).find((t) => t?.dataStatus)?.dataStatus || null;

// Keeps a limited list's dataStatus on the list itself (not enumerated), so
// a list with no team left in it can still say why it is short.
const withListStatus = (list, status) => {
  if (status && Array.isArray(list)) Object.defineProperty(list, "dataStatus", { value: status, configurable: true, writable: true });
  return list;
};

// LIVE Cerebro team query — every call hits Cerebro GraphQL via the
// /cerebro/live/teams/ backend endpoint. No cache_cb_* reads.
//
// `cerebroSports` is the cascade's primary input (e.g. ['intlmb']); the
// backend resolves each sport-key to the league_names + patterns ingest
// uses. `leagueNames` lets the cascade narrow an umbrella collection
// (e.g. just EuroLeague inside intlmb).
//
// While the team feed is down the answer can still be a 200: a saved list
// (dataStatus 'stale') or what CACHE already has ('unavailable'), with the
// server's own neutral sentence. Each team is then tagged with that
// dataStatus, as the football list's are, and so is the list, so every
// picker can say the list is limited (allocationModel.dataStatusNotice).
export const fetchCerebroTeams = async ({
  cerebroSports = [],
  leagueNames = [],
  leaguePatterns = [],
  teamQuery,
  gender,
} = {}) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.post(
      `${CACHE_SHARE_BASE_URL}/cerebro/live/teams/`,
      { cerebroSports, leagueNames, leaguePatterns, teamQuery, gender },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const teams = (response.data && response.data.teams) || [];
    const status = limitedListStatus(response.data?.dataStatus);
    return withListStatus(
      teams.map((t) => ({
        ...normalizeCerebroTeam(t),
        leagueName: t.leagueName || null,
        state: t.state || null,
        ...(status ? { dataStatus: status } : {}),
      })),
      status,
    );
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`,
      );
    }
    throw error;
  }
};

// LIVE CacheShare calculator — roster + per-player profiles fetched fresh
// from Cerebro on every Estimate Payouts click. Coaches see current-season
// stats, not yesterday's snapshot.
//
// Caller must pass `cerebroSports` (or explicit `leagueNames` / patterns)
// so the backend knows which Cerebro leagues to scope per-player stat
// aggregation against — same scoping the ingest pipeline used.
export const fetchCacheShareRangesCerebro = async ({
  rosterTeamId,
  teamName,
  cerebroSports = [],
  leagueNames = [],
  leaguePatterns = [],
  gender,
  revshareBudget,
  athleteIds,
  selectedOnly = false,
  importedAthleteIds,
  importedAthletes,
  lockedAllocations,
  hiddenIds,
}) => {
  const token = localStorage.getItem("token");
  try {
    const payload = {
      rosterTeamId,
      teamName,
      cerebroSports,
      leagueNames,
      leaguePatterns,
      // Rubric gender ('mbb' / 'wbb' or 'M' / 'F') for squad-scoped grids
      // that carry no sport key.
      gender,
      revshareBudget,
      athleteIds,
      selectedOnly,
      importedAthleteIds,
      // Cross-source: SR imports inside a Cerebro CacheShare grid get
      // live-fetched from SR by the backend and folded into the response.
      importedAthletes,
      ...lockFields({ lockedAllocations, hiddenIds }),
    };
    const response = await axios.post(
      `${CACHE_SHARE_BASE_URL}/calculator/cacheshare/cerebro/live/`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`,
      );
    }
    throw error;
  }
};

// Scoped player search — POSTs the resolved fetchPlan + name/position/
// jersey/class fields to the backend, which fans out to Cerebro (always)
// and SR (only when scope is narrow). Used by the Add Player modal to
// surface players across many teams in one shot. `q` is the modal's single
// search box: a name, or a position (G, PG, QB, ...) searched across the
// plan. Result rows are tagged with `source` so the import path routes each
// match correctly. `signal` (an AbortController's) cancels a search the
// user has moved on from.
export const searchPlayersInScope = async ({
  plan,
  q,
  firstName,
  lastName,
  position,
  jersey,
  classStanding,
  limit = 50,
  signal,
}) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.post(
      `${CACHE_SHARE_BASE_URL}/cacheshare/search-players/`,
      {
        plan,
        q,
        firstName,
        lastName,
        position,
        jersey,
        classStanding,
        limit,
      },
      { headers: { Authorization: `Bearer ${token}` }, signal },
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`,
      );
    }
    throw error;
  }
};

// LIVE Cerebro athlete search — used by the Add Player modal. Hits
// Cerebro's player table directly via /cerebro/live/athletes/.
export const searchAthletesCerebro = async ({
  name,
  gender,
  team,
  limit = 20,
  offset = 0,
}) => {
  const token = localStorage.getItem("token");
  try {
    const params = { name, gender, limit, offset };
    if (team && team.trim()) params.team = team.trim();
    const response = await axios.get(
      `${CACHE_SHARE_BASE_URL}/cerebro/live/athletes/`,
      {
        params,
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`,
      );
    }
    throw error;
  }
};

// Map a CacheShare basketball sport value to the Cerebro gender filter.
// Returns null for non-basketball sports (Cerebro trial is basketball-only).
export const cerebroGenderForSport = (sportValue) => {
  if (sportValue === "mbb") return "M";
  if (sportValue === "wbb") return "F";
  return null;
};

// A team originated from the Cerebro (EYBL) backend rather than SportRadar.
export const isCerebroTeam = (team) => team?.source === "cerebro";
export const isCacheTeam = (team) => team?.source === "cache_teams";
// A team originated from the Genius Sports feed (NCAA Football).
export const isGeniusTeam = (team) => team?.source === "genius";

// ---------------------------------------------------------------------------
// Genius Sports (NCAA Football) endpoints
// ---------------------------------------------------------------------------

// Football team list — the backend unions every current Genius competition
// (season) and tags each team with its competitionId, which the calculator
// call passes back so payouts run against the right season. The answer also
// carries the competitions (seasons, each with its label) and dataStatus:
// live, or "stale" while the Genius feed is paused and only the teams with
// saved stats are listed. Every team keeps its own `season` and
// `hasStoredStats` and is tagged with the list's dataStatus, so a picker that
// only sees the teams can still say the data is a saved copy.
export const fetchGeniusFootballTeamList = async ({ allTeams = false } = {}) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(
      `${CACHE_SHARE_BASE_URL}/genius/football/teams`,
      { params: allTeams ? { include: "all" } : {}, headers: { Authorization: `Bearer ${token}` } },
    );
    const data = response.data || {};
    const dataStatus = data.dataStatus || null;
    // Surface the school identity in the picker: the alias slot renders in
    // brackets after the name, so "Michigan (Wolverines)"; `location`
    // ("Ann Arbor, MI") feeds the picker's secondary line. The Genius feed
    // has no formal institution name, so nickname + location is the fullest
    // school identity available.
    const teams = (data.teams || []).map((t) => ({
      ...t,
      alias:
        t.alias ||
        (t.nickname &&
        t.nickname.toLowerCase() !== (t.name || "").toLowerCase()
          ? t.nickname
          : ""),
      source: "genius",
      dataStatus,
    }));
    return { teams, competitions: data.competitions || [], dataStatus };
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`,
      );
    }
    throw error;
  }
};

// The team list alone (the cascade's shape), each team tagged as above.
// `allTeams` keeps every saved team while the feed is paused (see
// executeFetchPlan).
export const fetchGeniusFootballTeams = async (options = {}) => (await fetchGeniusFootballTeamList(options)).teams;

// Football CacheShare calculator — roster + season stats live from Genius,
// payouts from the position-group allocation (budget split across
// QB/RB/WR/TE/OL/DL/LB/DB/ST, then exponentially by rating within each
// group). Rows carry `positionGroup` + `side` for the post-allocation
// offense / defense / special-teams filter, and `isPreviousSeason` /
// `seasonYear` (the PS chip); the answer carries `dataStatus` and
// `competition` (the season served).
export const fetchCacheShareRangesGeniusFootball = async ({
  rosterTeamId,
  competitionId,
  revshareBudget,
  athleteIds,
  selectedOnly = false,
  importedAthleteIds,
  // [{id, source, sourceTeamId, sourceCompetitionId, name, position,
  //   teamName, sportScore}] — genius imports get their real stat line
  // pulled from their origin team by the backend.
  importedAthletes,
  lockedAllocations,
  hiddenIds,
}) => {
  const token = localStorage.getItem("token");
  try {
    const payload = {
      rosterTeamId,
      competitionId,
      revshareBudget,
      athleteIds,
      selectedOnly,
      importedAthleteIds,
      importedAthletes,
      ...lockFields({ lockedAllocations, hiddenIds }),
    };
    const response = await axios.post(
      `${CACHE_SHARE_BASE_URL}/calculator/cacheshare/genius/football/`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`,
      );
    }
    throw error;
  }
};

// Estimate-payouts call for CACHE Partner teams (admin-managed).
// Response shape matches the SR live calculator so the grid can consume it
// without per-source branching.
export const fetchCacheShareRangesCacheTeams = async ({
  rosterTeamId,
  revshareBudget,
  athleteIds,
  selectedOnly = false,
  importedAthleteIds,
  importedAthletes,
  lockedAllocations,
  hiddenIds,
}) => {
  const token = localStorage.getItem("token");
  try {
    const payload = {
      rosterTeamId,
      revshareBudget,
      athleteIds,
      selectedOnly,
      importedAthleteIds,
      importedAthletes,
      ...lockFields({ lockedAllocations, hiddenIds }),
    };
    const response = await axios.post(
      `${CACHE_SHARE_BASE_URL}/calculator/cacheshare/cache-teams/`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`,
      );
    }
    throw error;
  }
};

// The allocation board's split of both pools: the revenue share and NIL
// payouts with the coach's locks, hidden players, each player's minimum
// (his CacheValue in revenue share, his NILx value in NIL) and the
// over-budget flags. Pure arithmetic on the rows the board already holds
// (no roster or stats calls), so the board posts it after every calculator
// answer and on every Distribute, lock and hide. Body and answer:
//   {sport, revshare: {budget, rows: [{id, payoutTeam, cacheValue}], hidden,
//    locks}, nil: {budget, rows: [{id, position, positionGroup, rating,
//    sportScore, visibilityScore, irlScore, nilxValue}], hidden, locks}}
//   -> {revshare: {rows: {id: {payoutAllocated, isLocked, floored, lift}},
//       allocatedTotal, remaining, overAllocated, lockedCount, flooredCount,
//       ignoredLocks},
//       nil: {rows: {id: {recommended, payout, isLocked, floored, lift}},
//       ...}}
// NIL's `recommended` is the NIL payout column (the model's split with no
// locks and no minimums); its `payout`, like revenue share's
// `payoutAllocated`, is the Allocated column. A pool is null when it was
// not sent or has no budget. `signal` (an AbortController's) cancels a post
// that a newer one replaces.
export const allocateCacheShare = async (body, { signal } = {}) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.post(
      `${CACHE_SHARE_BASE_URL}/calculator/cacheshare/allocate/`,
      body,
      { headers: { Authorization: `Bearer ${token}` }, signal },
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        `HTTP ${error.response.status}: ${
          error.response.data
            ? JSON.stringify(error.response.data)
            : error.response.statusText
        }`,
      );
    }
    throw error;
  }
};

// Only basketball sports (mbb / wbb) have Cerebro / EYBL teams available.
export const sportSupportsCerebro = (sportValue) =>
  sportValue === "mbb" || sportValue === "wbb";

// Execute a resolved cascade fetch plan (see taxonomy.resolveFetchPlan).
// Returns a merged, deduped list of teams tagged with their data source so
// downstream code (CacheShare team picker, calculator router) keeps working.
//
// For College: hits /sportsradar/teams and applies the conference filter
// client-side, since SR team docs already carry `conference`.
// For Pro/HS/College via Cerebro: fires ONE live Cerebro query — the
// /cerebro/live/teams/ endpoint expands cerebroSports + leagueNames into
// the full league list and returns matching teams in a single round trip.
//
// A Cerebro or CACHE Partner source that fails to answer reads as no teams,
// and so does a failed sub-plan of a multi plan. `throwOnError` (opt-in, the
// coach onboarding Team step) rethrows those failures instead, so the caller
// can offer Retry rather than an empty list; a multi plan then throws only
// when every sub-plan failed and keeps the teams of the ones that answered.
//
// While a provider is paused its team list holds only the teams with saved
// stats (the ones CacheShare can score). `allTeams` (opt-in, for pickers
// that bind a team rather than score it: onboarding, team assignment, team
// codes) asks for every saved team instead.
export const executeFetchPlan = async (plan, { throwOnError = false, allTeams = false } = {}) => {
  if (!plan) return [];
  const options = { throwOnError, allTeams };

  // Multi-source plan: fan out to each sub-plan in parallel and merge.
  // Used when the user skips the governing-body selector and we want to
  // surface teams from SR (NCAA D-I) AND Cerebro (NCAA D-II/D-III + NAIA
  // + NJCAA + JUCO + CCAA) under a single "all of College" pick. Each
  // sub-fetch tags its teams with `source` so downstream calculator
  // routing (isCerebroTeam) keeps working unchanged.
  if (plan.dataSource === "multi") {
    const subPlans = plan.plans || [];
    const failures = [];
    const results = await Promise.all(
      subPlans.map((p) => executeFetchPlan(p, options).catch((err) => {
        console.warn("Sub-plan fetch failed:", p, err);
        failures.push(err);
        return [];
      })),
    );
    if (throwOnError && subPlans.length > 0 && failures.length === subPlans.length) {
      throw failures[0];
    }
    const seen = new Set();
    const merged = [];
    for (const list of results) {
      for (const t of list || []) {
        if (!t || !t.id) continue;
        // Dedupe across sources by team id AND squad scope: one Cerebro
        // program is several squads (17U / 16U / 15U) that share an id and
        // must all stay in the list.
        const key = `${t.id}|${t.leagueKey || t.leagueName || ""}`;
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(t);
      }
    }
    // A sub-list a feed limited limits the union too (a live sub-list's
    // tagged rows say nothing about the others).
    return withListStatus(merged, results.map((list) => limitedListStatus(listStatusOf(list))).find(Boolean) || null);
  }

  // CACHE Partners — admin-managed teams. Tag each row so downstream
  // routing (effectiveLeague derivation, calculator dispatch) can detect
  // the source without inspecting the team's league_key prefix.
  if (plan.dataSource === "cache_teams") {
    const teams = await fetchCacheTeams({
      gender: plan.gender,
      partner: plan.partner,
    }, options);
    return (teams || []).map((t) => ({
      ...t,
      cacheLeague: t.league_key || null,
      source: "cache_teams",
    }));
  }

  // Genius Sports (NCAA Football): one call returns every team across the
  // current competitions, already tagged with source + competitionId.
  if (plan.dataSource === "genius") {
    return fetchGeniusFootballTeams({ allTeams });
  }

  if (plan.dataSource === "sportsradar") {
    const rawTeams = await fetchTeamOptions({ sport: plan.srSport });
    // Tag each team with its srSport so downstream consumers (notably the
    // onboarding wizard's `effectiveLeague` derivation) can map back to
    // the CACHE league code (mbb -> ncaamb, wbb -> ncaawb). Cerebro teams
    // already carry `cerebroLeague` for the same purpose.
    const teams = (rawTeams || []).map((t) => ({ ...t, srSport: plan.srSport }));
    // The cascade Step 6 labels are short (e.g. "ACC", "SEC", "C-USA"); SR
    // returns the long name in `conference` ("Atlantic Coast Conference")
    // and the short alias in `conferenceAlias` ("ACC"). Match against both
    // after collapsing case, whitespace, and dashes so "C-USA" can hit
    // either "C-USA" alias or "Conference USA" name; "Sun Belt" hits
    // "Sun Belt Conference"; "Big Ten" hits both "Big Ten Conference"
    // name and "BIG-10" alias.
    const norm = (s) =>
      (s || "")
        .toString()
        .toLowerCase()
        .replace(/[-\s]+/g, "")
        .trim();

    const matchTeam = (t, wanted) => {
      const candidates = [norm(t.conferenceAlias), norm(t.conference)].filter(
        Boolean,
      );
      if (!candidates.length) return false;
      const w = norm(wanted);
      if (!w) return false;
      return candidates.some((c) => c === w || c.includes(w) || w.includes(c));
    };

    // Explicit single-conference pick narrows hardest.
    if (plan.conference) {
      return (teams || []).filter((t) => matchTeam(t, plan.conference));
    }
    // Sub-division (P4 / G5 / HBCU / FCS) carries a conferenceFilter
    // array of conferences allowed in that bucket. Empty array means
    // "no narrowing" (e.g. FCS for basketball is a no-op).
    if (plan.conferenceFilter && plan.conferenceFilter.length) {
      return (teams || []).filter((t) =>
        plan.conferenceFilter.some((w) => matchTeam(t, w)),
      );
    }
    return teams;
  }

  if (plan.dataSource === "cerebro") {
    const sports = plan.cerebroSports || [];
    const leagueNames = plan.leagueNames || [];
    if (!sports.length && !leagueNames.length) return [];
    // "Any league" (cbx_any) has no competition scope: the server finds
    // programs by name, so there is nothing to fetch until the user types.
    if (plan.anyLeague && (plan.teamQuery || "").trim().length < 2) return [];
    let teams = [];
    try {
      teams = await fetchCerebroTeams({
        cerebroSports: sports,
        leagueNames,
        teamQuery: plan.anyLeague ? plan.teamQuery : undefined,
        gender: plan.gender,
      });
    } catch (e) {
      if (throwOnError) throw e;
      console.warn("Live Cerebro team fetch failed:", e);
      return [];
    }
    // A list the team feed limited stays limited, whatever narrows it below.
    const status = listStatusOf(teams);
    const keep = (list) => withListStatus(list, status);
    // Tag each team with its primary cerebroSport so downstream code (Add
    // Player modal source-tagging, calculator routing) keeps working —
    // every team in this batch belongs to the same cascade slice.
    const primarySport = sports[0] || null;
    const tagged = teams.map((t) => ({
      ...t,
      cerebroLeague: primarySport,
    }));
    // One row per squad: a Cerebro program is several squads (17U / 16U /
    // 15U) that share the program id, so the key carries the squad scope.
    const seen = new Set();
    const deduped = [];
    for (const t of tagged) {
      if (!t.id) continue;
      const key = `${t.id}|${t.leagueKey || t.leagueName || ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(t);
    }
    // State HS Association cascade. Each State HS sub-division carries:
    //   - statePattern:      full state name (e.g. "Alabama") that prefixes
    //                        the leagueName for events from that state.
    //   - federationAcronym: e.g. "AHSAA" — disambiguates shared acronyms
    //                        (AHSAA covers AL+AR; statePattern is what tells
    //                        them apart).
    //   - classFilter:       optional Step 5b class/division pick (e.g.
    //                        "Class 1A"). Filters leagueName to events whose
    //                        title contains that class string.
    // Conference narrowing — applied here since Cerebro doesn't accept a
    // conference filter at query time. The /cerebro/live/teams response
    // includes each team's most recent conference (via the
    // event_conference_team join), so a quick client-side filter narrows
    // the picker without another round trip. Case-insensitive contains
    // match handles both "ACC" / "Atlantic Coast Conference" naming.
    if (plan.conferenceFilter && plan.conferenceFilter.length) {
      const wanted = plan.conferenceFilter.map((c) =>
        (c || "").toLowerCase(),
      );
      const matches = (t) => {
        const conf = (t.conference || "").toLowerCase();
        if (!conf) return false;
        return wanted.some((w) => conf === w || conf.includes(w));
      };
      const narrowed = deduped.filter(matches);
      // Fall back to the broader list if the join returned no conference
      // info (e.g. Cerebro schema didn't expose event_conference_team) so
      // the user isn't left with an empty picker.
      if (narrowed.length || deduped.some((t) => t.conference)) {
        return keep(narrowed);
      }
    }

    if (plan.statePattern || plan.federationAcronym || plan.classFilter) {
      return keep(deduped.filter((t) => {
        const ln = t.leagueName || "";
        if (plan.statePattern && !ln.includes(plan.statePattern)) return false;
        if (plan.federationAcronym &&
            !ln.includes(`(${plan.federationAcronym})`)) return false;
        if (plan.classFilter && !ln.includes(plan.classFilter)) return false;
        return true;
      }));
    }
    // Fallback: legacy `stateFilter` abbreviation (used when statePattern
    // isn't populated, e.g. by callers that haven't moved to the new shape).
    if (plan.stateFilter) {
      const abbr = plan.stateFilter.toUpperCase();
      return keep(deduped.filter((t) => {
        if ((t.state || "").toUpperCase() === abbr) return true;
        const ln = (t.leagueName || "").toUpperCase();
        return ln.includes(`(${abbr})`);
      }));
    }
    return keep(deduped);
  }

  return [];
};


// US state high school associations with competitions on Cerebro, each with
// its catalogue keys and classes. Drives the State HS picker so the state
// list is never hand-typed.
export const fetchCerebroStates = async (gender) => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${CACHE_SHARE_BASE_URL}/cerebro/competitions/states/`, {
    params: gender ? { gender } : {},
    headers: { Authorization: `Bearer ${token}` },
  });
  return (response.data && response.data.states) || [];
};

// Any league Cerebro exposes: search the competition catalogue by name.
// Each row's `key` is a league key the backend resolves on the fly
// (cascade cerebroSports, team assignment, roster loads).
export const fetchCerebroCompetitions = async ({ q = "", gender, level, limit = 30 } = {}) => {
  const token = localStorage.getItem("token");
  try {
    const params = { q, limit };
    if (gender) params.gender = gender;
    if (level) params.level = level;
    const response = await axios.get(`${CACHE_SHARE_BASE_URL}/cerebro/competitions/`, {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return (response.data && response.data.competitions) || [];
  } catch (error) {
    console.warn("Cerebro competitions search failed:", error?.message || error);
    return [];
  }
};

// Cerebro scope for a team's calculator call: a squad picked from a league
// list carries its exact "competition|division" key; otherwise the cascade
// plan's leagues, else the league the team was tagged with.
export const cerebroScopeFor = (team, plan, sport = "") =>
  team?.leagueKey
    ? { cerebroSports: [], leagueNames: [team.leagueKey], gender: sport }
    : {
        cerebroSports: plan?.cerebroSports?.length ? plan.cerebroSports : team?.cerebroLeague ? [team.cerebroLeague] : [],
        leagueNames: plan?.leagueNames?.length ? plan.leagueNames : team?.leagueName ? [team.leagueName] : [],
        gender: sport,
      };

// One CacheShare calculator call for a team, routed by where the team comes
// from: Genius (NCAA football), CACHE Partner, live Cerebro, live SportRadar
// (NCAA D-I basketball) or the cached SportRadar endpoint. `sport` is the
// legacy key (mbb / wbb / fb); `params` are the calculator's own fields
// (revshareBudget, importedAthleteIds, importedAthletes, lockedAllocations,
// hiddenIds, ...).
export const fetchTeamRanges = (team, { sport = "", scope = {}, ...params } = {}) => {
  const rosterTeamId = team?.id || undefined;
  if (isGeniusTeam(team)) {
    return fetchCacheShareRangesGeniusFootball({ rosterTeamId, competitionId: team.competitionId || undefined, ...params });
  }
  if (isCacheTeam(team)) return fetchCacheShareRangesCacheTeams({ rosterTeamId, ...params });
  if (isCerebroTeam(team)) {
    return fetchCacheShareRangesCerebro({
      rosterTeamId,
      teamName: team.name || team.market || "",
      cerebroSports: scope.cerebroSports || [],
      leagueNames: scope.leagueNames || [],
      gender: scope.gender || sport,
      ...params,
    });
  }
  const liveSr = sport === "mbb" || sport === "wbb";
  return (liveSr ? fetchCacheShareRangesSportsradarLive : fetchCacheShareRangesSportsradar)({ rosterTeamId, sport, ...params });
};
